import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Copy,
  Download,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import { toast } from "sonner";
import { format } from "timeago.js";
import Loader from "@/components/loader/Loader";
import { cn } from "@/lib/utils";
import { ErrorState } from "@/components/ErrorState";
import { formatEvmHumanBalance } from "@/utils";

function statusTone(status: string) {
  switch (status?.toLowerCase()) {
    case "completed":
      return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
    case "pending":
      return "bg-amber-500/10 text-amber-500 border-amber-500/20";
    case "processing":
      return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    case "failed":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
}

const copy = (value?: string) => {
  if (!value) return;
  navigator.clipboard.writeText(value);
  toast.success("Copied", { description: "Copied to clipboard" });
};

const openExplorer = (txHash?: string, explorerUrl?: string) => {
  if (!txHash || !explorerUrl) return;

  const baseUrl = explorerUrl.replace(/\/+$/, "");
  const url = `${baseUrl}/tx/${txHash}`;

  window.open(url, "_blank", "noopener,noreferrer");
};

export default function AdminTransactionReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["tx:receipt", id],
    queryFn: async () => {
      const res = await makeRequest.get(`/transaction/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const tx = data;

  console.log("TRANSACTION", tx);

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  if (isLoading) return <Loader type="bounce" />;

  // if (!tx) {
  //   return (
  //     <div className="p-10 text-center text-muted-foreground">
  //       Transaction not found
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <div className="max-w-6xl mx-auto lg:px-6 py-6">
        {/* HEADER ACTION BAR */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <Button
            className="hidden lg:flex"
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw
                className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")}
              />
              Refresh
            </Button>

            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Receipt
            </Button>
          </div>
        </div>

        {/* GRID LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden">
          {/* MAIN RECEIPT */}
          <div className="lg:col-span-2 space-y-6">
            {/* STATUS HEADER (receipt anchor) */}
            <Card className="border-muted/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground uppercase tracking-wider">
                      Transaction Receipt
                    </p>

                    <p className="font-mono mt-1 break-all">#{tx?.id}</p>
                  </div>

                  <Badge className={statusTone(tx?.status)}>{tx?.status}</Badge>
                </div>

                <div className="flex justify-between gap-4">
                  {/* AMOUNT CORE */}
                  <div className="py-4 border-t border-b">
                    <p className="text-muted-foreground text-sm">
                      Total Amount
                    </p>

                    <h1 className="text-3xl font-bold tracking-tight">
                      {tx?.asset?.currency}{" "}
                      {formatEvmHumanBalance(
                        Number(tx?.amount),
                        tx?.asset?.token?.isNative,
                      )}
                    </h1>

                    <p className="text-sm text-muted-foreground mt-1">
                      {tx?.walletAsset?.tokenName}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 py-4 border-t border-b">
                    <p className="text-muted-foreground text-sm">
                      Blockchain and Standard
                    </p>
                    <div>
                      {tx?.asset?.token?.standard} on {tx?.asset?.network?.code}
                    </div>
                  </div>
                </div>

                {/* QUICK META */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-medium capitalize">{tx?.type}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Method</p>
                    <p className="font-medium">{tx?.source}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p>{format(tx.createdAt)}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Updated</p>
                    <p>{format(tx.updatedAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* USER + SYSTEM DETAILS */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>User Identity</CardTitle>
                  <CardDescription>
                    Account linked to transaction
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 flex flex-col gap-1 text-sm">
                  <div>
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">
                        {tx.user?.firstName} {tx.user?.lastName}
                      </p>
                    </div>

                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium">{tx.user?.email}</p>
                    </div>

                    <div>
                      <p className="text-muted-foreground">User ID</p>
                      <p className="font-medium">{tx.user?.uid}</p>
                    </div>
                  </div>
                  <Link to={`/admin/users/${tx?.user?.id}`}>
                    <Button>View User</Button>
                  </Link>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>System Trace</CardTitle>
                  <CardDescription>Internal execution metadata</CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Transaction ID</p>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-mono text-xs truncate flex-1 min-w-0">
                        {tx.id}
                      </p>

                      <Copy
                        className="w-3 h-3 shrink-0 cursor-pointer"
                        onClick={() => copy(tx.id)}
                      />
                    </div>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Status Flow</p>
                    <p className="font-medium capitalize">{tx.status}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Source</p>
                    <p>{tx.source}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* HASH / EXTERNAL */}
            {tx.txHash && (
              <Card>
                <CardHeader>
                  <CardTitle>External Verification</CardTitle>
                  <CardDescription>
                    Blockchain / external ledger reference
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* TRANSACTION HASH */}
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">
                      Transaction Hash
                    </p>

                    <p className="font-mono text-xs break-all">{tx.txHash}</p>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center gap-2">
                    {/* MOBILE: Full explorer button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 sm:hidden"
                      onClick={() =>
                        openExplorer(tx.txHash, tx.asset?.network?.explorerUrl)
                      }
                      disabled={!tx.asset?.network?.explorerUrl}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Check on Explorer
                    </Button>

                    {/* DESKTOP: Icon-only explorer button */}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="hidden sm:inline-flex"
                      onClick={() =>
                        openExplorer(tx.txHash, tx.asset?.network?.explorerUrl)
                      }
                      disabled={!tx.asset?.network?.explorerUrl}
                      title="View transaction on explorer"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>

                    {/* COPY */}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => copy(tx.txHash)}
                      title="Copy transaction hash"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT SIDEBAR — STICKY SUMMARY */}
          <div className="space-y-6 lg:sticky lg:top-6 h-fit">
            <Card className="border-muted/50">
              <CardHeader>
                <CardTitle>Receipt Summary</CardTitle>
                <CardDescription>
                  Immutable transaction snapshot
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={statusTone(tx.status)}>{tx.status}</Badge>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium text-right break-all">
                    {tx?.asset?.currency}{" "}
                    {formatEvmHumanBalance(
                      tx?.amount,
                      tx?.asset?.token?.isNative,
                    )}
                  </span>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Type</span>
                  <span className="capitalize">{tx?.type}</span>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Age</span>
                  <span>{format(tx.createdAt)}</span>
                </div>
              </CardContent>
            </Card>

            {/* ADMIN ACTION ZONE */}
            <Card className="border-muted/50">
              <CardHeader>
                <CardTitle>Admin Actions</CardTitle>
                <CardDescription>
                  Restricted operational controls
                </CardDescription>
              </CardHeader>

              <CardContent className="flex flex-col gap-2">
                <Button variant="outline">Flag Transaction</Button>
                <Button variant="outline">Audit Trail</Button>
                <Button variant="destructive">Reverse</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
